import { ALL_ENTRIES_FILTER_VALUE, NONE_ENTRIES_FILTER_VALUE, LeaguesTypes } from '../constants'

const PRODUCT_TYPE_ORDER = [
  LeaguesTypes.EVENT,
  LeaguesTypes.LEAGUE,
  LeaguesTypes.NON_GOLF_EVENT,
  LeaguesTypes.TRIP,
  LeaguesTypes.DRIVE_CHIP_PUTT,
]

const sortProductTypes = (productTypes) => {
  const sorted = {}
  PRODUCT_TYPE_ORDER.forEach(type => {
    if (type in productTypes) {
      sorted[type] = productTypes[type]
    }
  })

  // Unknown types are being appended to the end of the list
  Object.keys(productTypes).forEach(type => {
    if (!(type in sorted)) {
      sorted[type] = productTypes[type]
    }
  })
  return sorted
}

export const reducerName = 'LEAGUES_FILTERS'

const ActionTypes = {
  SET_LEAGUES_FILTER_CATEGORY: 'SET_LEAGUES_FILTER_CATEGORY',
  SET_LEAGUES_FILTER_SEASON: 'SET_LEAGUES_FILTER_SEASON',
  SET_LEAGUES_FILTER_TOUR: 'SET_LEAGUES_FILTER_TOUR',
  SET_LEAGUES_FILTER_PRODUCT_TYPE: 'SET_LEAGUES_FILTER_PRODUCT_TYPE',
  SET_LEAGUES_FILTER_TEXT_SEARCH: 'SET_LEAGUES_FILTER_TEXT_SEARCH',
}

export const initialFilterChoices = {
  category: ALL_ENTRIES_FILTER_VALUE,
  productType: ALL_ENTRIES_FILTER_VALUE,
  season: ALL_ENTRIES_FILTER_VALUE,
  tour: ALL_ENTRIES_FILTER_VALUE,
  textSearch: '',
}

const initialState = {
  categories: [],
  productTypes: [],
  seasons: [],
  currentSeason: {},
  tours: [],
  ...initialFilterChoices,
}

const LeaguesFilterValuesReducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_LEAGUES_FILTER_CATEGORY:
      return {
        ...state,
        category: action.category,
      }

    case ActionTypes.SET_LEAGUES_FILTER_SEASON:
      return {
        ...state,
        season: action.season,
      }

    case ActionTypes.SET_LEAGUES_FILTER_TOUR:
      return {
        ...state,
        tour: action.tour,
      }

    case ActionTypes.SET_LEAGUES_FILTER_PRODUCT_TYPE:
      return {
        ...state,
        productType: action.productType,
      }

    case ActionTypes.SET_LEAGUES_FILTER_TEXT_SEARCH:
      return {
        ...state,
        textSearch: action.textSearch,
      }

    default:
      return { ...state }
  }
}

export const selectLeaguesCategories = state => state?.[reducerName]?.categories
export const selectLeaguesProductTypes = state => sortProductTypes(state?.[reducerName]?.productTypes ?? {})
export const selectLeaguesSeasons = state => state?.[reducerName]?.seasons
export const selectLeaguesTours = state => state?.[reducerName]?.tours
export const selectCategory = state => state?.[reducerName]?.category
export const selectSeason = state => state?.[reducerName]?.season
export const selectTour = state => state?.[reducerName]?.tour
export const selectProductType = state => state?.[reducerName]?.productType
export const selectTextSearch = state => state?.[reducerName]?.textSearch
export const selectCurrentSeason = state => state?.[reducerName]?.currentSeason
export const selectCurrentSeasonID = state => Object.keys(state?.[reducerName]?.currentSeason)[0]

export const mapToDropdown = (filtersDict) =>
  Object.entries(filtersDict).map(([ key, value ]) => ({ value: key, label: value }))

// Categories carry { label, position } values (vs plain strings for other filter types).
// Sorting is done on the entries array to bypass JS numeric-key ordering on the raw object.
// The 'all' entry added by withAllEntry has no position, so it receives -1 and sorts first.
export const mapCategoriesToDropdown = (categoriesDict) =>
  Object.entries(categoriesDict)
    .sort(([ , a ], [ , b ]) => (a?.position ?? -1) - (b?.position ?? -1))
    .map(([ key, value ]) => ({ value: key, label: value?.label ?? value }))

const setSeasonAction = (season) => ({
  type: ActionTypes.SET_LEAGUES_FILTER_SEASON,
  season: season,
})

export const setSeason = (season) => (dispatch) => {
  dispatch(setSeasonAction(season))
}

const setTourAction = (tour) => ({
  type: ActionTypes.SET_LEAGUES_FILTER_TOUR,
  tour: tour,
})

export const setTour = (tour) => (dispatch) => {
  dispatch(setTourAction(tour))
}


const setCategoryAction = (category) => ({
  type: ActionTypes.SET_LEAGUES_FILTER_CATEGORY,
  category: category,
})

export const setCategory = (category) => (dispatch) => {
  dispatch(setCategoryAction(category))
}

const setProductTypeAction = (productType) => ({
  type: ActionTypes.SET_LEAGUES_FILTER_PRODUCT_TYPE,
  productType: productType,
})

export const setProductType = (productType) => (dispatch) => {
  dispatch(setProductTypeAction(productType))
}

const setTextSearchAction = (textSearch) => ({
  type: ActionTypes.SET_LEAGUES_FILTER_TEXT_SEARCH,
  textSearch: textSearch,
})

export const setTextSearch = (textSearch) => (dispatch) => {
  dispatch(setTextSearchAction(textSearch))
}

export const resetFilters = () => (dispatch) => {
  dispatch(setTextSearchAction(initialFilterChoices.textSearch))
  dispatch(setCategoryAction(initialFilterChoices.category))
  dispatch(setProductTypeAction(initialFilterChoices.productType))
  dispatch(setSeasonAction(initialFilterChoices.season))
  dispatch(setTourAction(initialFilterChoices.tour))
}

export const serializeFilters = (
  productType = ALL_ENTRIES_FILTER_VALUE,
  category = ALL_ENTRIES_FILTER_VALUE,
  season = ALL_ENTRIES_FILTER_VALUE,
  tour = ALL_ENTRIES_FILTER_VALUE,
  textSearch = '',
) => {

  return {
    'current_active_filters[filter_by_season]': season,
    'current_active_filters[filter_by_category]': category,
    'current_active_filters[filter_by_tour]': tour,
    'current_active_filters[filter_by_product_type]': productType,
    'current_active_filters[search_element]': textSearch,
  }
}

export const namedReducer = {[reducerName]: LeaguesFilterValuesReducer}

export const getInitialState = ({categories, productTypes, seasons, currentSeason, tours}) => {
  let enhancedSeasons = { ...seasons }
  let enhancedCurrentSeason = {}
  const seasonsIDs = Object.keys(seasons)
  const currentSeasonIDs = Object.keys(currentSeason || {})

  // Mark and reorder it as current season
  if (currentSeason && currentSeasonIDs.length === 1) {
    const currentSeasonID = currentSeasonIDs[0]
    const currentSeasonName = seasons[currentSeasonID]

    if (seasonsIDs.includes(currentSeasonID)) {
      enhancedCurrentSeason = { [currentSeasonID]: `${currentSeasonName} (${window.I18n.t('season.current')})` }
      enhancedSeasons = {
        ...enhancedSeasons,
        ...enhancedCurrentSeason,
      }
    }
  }

  return {
    ...initialFilterChoices,
    categories,
    productTypes,
    seasons: enhancedSeasons,
    currentSeason: enhancedCurrentSeason,
    tours,
  }
}

const appendDictEntryInFront = (dict, key, value) => {
  const deepClonedDict = { ...dict }
  delete deepClonedDict[key]

  return {
    [ key ]: value,
    ...deepClonedDict,
  }
}

export const withAllEntry = (filtersDict) => {
  return appendDictEntryInFront(
    filtersDict,
    ALL_ENTRIES_FILTER_VALUE,
    window.I18n.t('clone_leagues_sessions.dropdowns.all'),
  )
}

export const withSeasonsEntries = (filtersDict, currentSeasonID, currentSeason) => {
  let clonedFiltersDict = { ... filtersDict }

  clonedFiltersDict = appendDictEntryInFront(
    clonedFiltersDict,
    NONE_ENTRIES_FILTER_VALUE,
    window.I18n.t('clone_leagues_sessions.dropdowns.no_season'),
  )

  if (currentSeason && currentSeasonID) {
    clonedFiltersDict = appendDictEntryInFront(
      clonedFiltersDict,
      currentSeasonID,
      currentSeason[currentSeasonID],
    )
  }

  return clonedFiltersDict
}

export default LeaguesFilterValuesReducer
